import React, { useEffect } from "react";
import qnetLogo from "../../assets/qnet-with-text-logo.svg";
import { useMemo, useState } from "react";
import router from "../../components/routing/router";

interface Props {
  phoneNumber: string;
  areaCode: string;
  verificationCode: string;
  setEnterCode: (enterCode: boolean) => void;
  setVerificationCode: (verificationCode: string) => void;
  checkOTP: () => void;
  sendOTP: () => void;
  setAreaCode: (areaCode: string) => void;
  setPhoneNumber: (phoneNumber: string) => void;
  email: string;
  isUsingEmail: boolean;
  sendOTPEmail: () => void;
  isVerifyButtonDisabled: boolean;
}

export function EnterOTP(props: Props) {
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [timer, setTimer] = useState(0);
  const inputRefs: React.RefObject<any>[] = useMemo(
    () =>
      Array(code.length)
        .fill(0)
        .map((i) => React.createRef()),
    [],
  );

  const updateVerificationCode = (e: string, index: number) => {
    const newCode = [...code];
    newCode[index] = e;

    setCode(newCode);

    props.setVerificationCode(newCode.join(""));

    // Auto focus the next input box after setting value
    if (inputRefs[index + 1] && newCode[index].length > 0)
      inputRefs[index + 1].current.focus();
    if (inputRefs[index - 1] && newCode[index].length <= 0)
      inputRefs[index - 1].current.focus();
  };

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [props.verificationCode]);

  const handleResendCode = () => {
    setIsButtonDisabled(true);
    setTimer(30); // Set the timer for 30 seconds
    if (props.isUsingEmail) {
      props.sendOTPEmail();
    }else {
      props.sendOTP();
    }

    const countdown = setInterval(() => {
      setTimer((prevTimer) => {
        if (prevTimer <= 1) {
          clearInterval(countdown);
          setIsButtonDisabled(false);
          return 0;
        }
        return prevTimer - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    props.setAreaCode(props.areaCode);
    props.setPhoneNumber(props.phoneNumber);
  }, [props.phoneNumber, props.areaCode]);

  const handleKeyDown = (event: any) => {
    if (event.key === "Enter") {
      setTimeout(() => {
        props.checkOTP();
      }, 100);
    }
  };

  return (
    <div className="flex aspect-square w-full max-[768px]:w-[100%]">
      <div className="flex w-[100%] max-w-[500px] flex-1 flex-col max-[768px]:max-w-[100%]">
        <div className="flex items-center justify-start pt-[5vh]">
          <img
            className="h-[100%] max-h-[70px]"
            src={qnetLogo}
            alt="QNET Logo"
          />
        </div>

        <div className="flex flex-1 flex-col items-baseline justify-center">
          <div>
            <span
              className="flex cursor-pointer flex-row items-center text-sm text-black hover:underline"
              onClick={() => window.location.reload()}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="mr-2 size-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6.75 15.75 3 12m0 0 3.75-3.75M3 12h18"
                />
              </svg>
              <span>Back</span>
            </span>
          </div>
          <div className="flex w-[100%] items-center justify-start max-[768px]:justify-center">
            <span className="text-[32px] text-[black]">
              <b>Enter the code</b>
            </span>
          </div>
          { props.isUsingEmail ?
            <div className="flex w-[100%] items-center justify-start max-[768px]:justify-center">
              <span className="text-[14px] text-[#333333]">{`We sent a 6-digit confirmation code to ${props.email}. It expires shortly, so enter it soon.`}</span>
            </div>
            :
            <div className="flex w-[100%] items-center justify-start max-[768px]:justify-center">
              <span className="text-[14px] text-[#333333]">{`We sent a 6-digit confirmation code to ${props.areaCode} ${props.phoneNumber}. It expires shortly, so enter it soon.`}</span>
            </div>
          }

          <div className="flex w-[100%] flex-col pt-[16px]">
            <div className="flex items-center gap-x-[10px]">
              {code.map((c: string, index: number) => {
                return (
                  <input
                    onChange={(e) =>
                      updateVerificationCode(e.target.value, index)
                    }
                    ref={inputRefs[index]}
                    key={index}
                    type="text"
                    placeholder=""
                    maxLength={1}
                    className="input input-bordered h-[100px] w-[16%] border-[grey] bg-[white] text-center text-[38px] font-bold text-[black]"
                  />
                );
              })}
            </div>
          </div>

          <div className="flex w-full items-center justify-start pt-5">
            <button
              onClick={() => props.checkOTP()}
              className="btn w-full border-none bg-[#fedd00] text-black hover:bg-[#fedd00]"
              disabled={props.isVerifyButtonDisabled}
            >
              {props.isUsingEmail ? "Verify Email" : "Verify Device"}
            </button>
          </div>
          <div className="flex w-full items-center justify-start pt-2">
            <button
              onClick={handleResendCode}
              className="w-full text-[14px] text-[#333333]"
              disabled={isButtonDisabled}
            >
              {isButtonDisabled
                ? `Resend Code in ${timer}s`
                : "Resend Verification Code"}
            </button>
          </div>
        </div>

        <div className="border[grey] border-b-[1px] pt-[50px]"></div>

        <div className="flex items-center justify-center pb-[5vh] pt-[14px] text-[#fedd00] hover:cursor-pointer">
          <a href="mailto:help@teamqualityservices.com">Need Help?</a>
        </div>
      </div>
    </div>
  );
}
